import React, { useState, useEffect } from 'react';
import './SearchBar.css';

interface OsmLocation {
  lat: number;
  lon: number;
  zoom: number;
  display_name: string;
}

interface SearchBarProps {
  onSelectLocation: (result: { lat: number; lon: number; zoom?: number; display_name: string }) => void;
}

function parseOsmUrl(url: string): OsmLocation | null {
  const match = url.match(/#(?:map=)?(\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)/);
  if (!match) return null;

  const zoom = parseFloat(match[1]);
  const lat = parseFloat(match[2]);
  const lon = parseFloat(match[3]);
  if (isNaN(zoom) || isNaN(lat) || isNaN(lon)) return null;

  return { lat, lon, zoom, display_name: `${lat}, ${lon}` };
}

const SearchBar: React.FC<SearchBarProps> = ({ onSelectLocation }) => {
  const [hasError, setHasError] = useState(false);

  // Paste an OSM URL anywhere on the page to jump to that location.
  useEffect(() => {
    const handleGlobalPaste = (e: ClipboardEvent) => {
      const active = document.activeElement;
      const isEditable =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active as HTMLElement | null)?.isContentEditable;
      if (isEditable) return;

      const pastedText = e.clipboardData?.getData('text') ?? '';
      if (!pastedText) return;

      const location = parseOsmUrl(pastedText);
      if (location) {
        setHasError(false);
        onSelectLocation(location);
      } else {
        setHasError(true);
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, [onSelectLocation]);

  if (!hasError) return null;

  return (
    <div className="search-container">
      <div className="search-error">Couldn't find a location in that URL</div>
    </div>
  );
};

export default SearchBar;
